import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Color } from "../../../constants/color";
import Suggerimento from "../../tooltip/suggerimento";
import {
  CampoCorreggibile,
  CellaCorretta,
  EmployeeImportAnalysis,
  EmployeeImportRow,
} from "../../../types";

/*
 Le righe del foglio, come le vede chi le deve sistemare.

 E' qui che si correggono i dati, non nella chat: l'operatore ha davanti la riga e il campo, e
 dettarli all'assistente — trenta e-mail, trenta messaggi — era la parte piu' faticosa del
 caricamento guidato. Una cella si corregge con un clic; la stessa mansione, sede o data si da'
 a piu' righe insieme selezionandole.

 Ogni correzione va al backend, che rifa' l'analisi: l'esito della riga, i conteggi e il
 colore delle celle arrivano da li', non si ricalcolano qui.
*/

type Filtro = "tutte" | "TO_CREATE" | "TO_UPDATE" | "REJECTED" | "da-completare";

type StatoCella = "bloccante" | "mancante" | "cambiata" | "normale";

type Colonna = {
  campo: CampoCorreggibile;
  titolo: string;
  valore: (riga: EmployeeImportRow) => string | null;
  // Come il backend nomina questo campo nei problemi e nei cambiamenti.
  nomiNelBackend: string[];
  tipo: "text" | "date" | "email";
  larghezza: string;
};

// I campi che ha senso dare uguali a piu' persone insieme.
const CAMPI_DI_GRUPPO: CampoCorreggibile[] = [
  "mansione",
  "sede",
  "dataassunzione",
];

const chiaveDi = (riga: EmployeeImportRow) => `${riga.sheet ?? ""}#${riga.row}`;

const illeggibile = (riga: EmployeeImportRow) =>
  riga.problems.some((problema) => problema.code === "ROW_UNREADABLE");

const daCompletare = (riga: EmployeeImportRow) =>
  riga.outcome !== "REJECTED" &&
  riga.problems.some((problema) => problema.severity === "TO_COMPLETE");

// yyyy-MM-dd -> dd/MM/yyyy
const comeData = (valore: string | null) => {
  if (!valore) {
    return valore;
  }
  const [anno, mese, giorno] = valore.split("-");
  return giorno && mese && anno ? `${giorno}/${mese}/${anno}` : valore;
};

const coloreEsito = {
  TO_CREATE: Color.GREEN,
  TO_UPDATE: Color.BLUE,
  REJECTED: Color.RED,
};

const sfondoCella: Record<StatoCella, string> = {
  bloccante: "#FDE2E1",
  mancante: "#FFF4D6",
  cambiata: "#E3EEFB",
  normale: Color.NONE,
};

const Contenitore = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
`;

const Filtri = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.8rem 1rem 0.5rem;
`;

const Chip = styled.button<{ $colore: string; $attivo: boolean }>`
  padding: 0.25rem 0.7rem;
  border: 2px solid ${({ $colore }) => $colore};
  border-radius: 1rem;
  background-color: ${({ $colore, $attivo }) =>
    $attivo ? $colore : Color.WHITE};
  color: ${({ $colore, $attivo }) => ($attivo ? Color.WHITE : $colore)};
  font-family: inherit;
  font-size: 0.85rem;
  white-space: nowrap;
  cursor: pointer;
`;

const ProblemaFile = styled.p`
  margin: 0 1rem 0.5rem;
  padding: 0.5rem 0.7rem;
  border-radius: 0.5rem;
  background-color: ${Color.PINK};
  color: ${Color.DARK_GREY};
  font-size: 0.85rem;
`;

const BarraGruppo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin: 0 1rem 0.5rem;
  padding: 0.5rem 0.7rem;
  border-radius: 0.5rem;
  background-color: ${Color.LIGHTER_BLUE};
  font-size: 0.85rem;
`;

const Scorrimento = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 1rem 1rem;
`;

/*
 Larghezze fisse: aprendo una cella il campo non deve allargare la colonna. Con le larghezze
 calcolate dal contenuto la tabella si ridisponeva al primo clic, e il clic sulla riga dopo
 finiva sulla colonna accanto.
*/
const Tabella = styled.table`
  width: 100%;
  min-width: 60rem;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.85rem;
  color: ${Color.DARK_GREY};

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 0.45rem 0.4rem;
    background-color: ${Color.WHITE};
    border-bottom: 2px solid ${Color.LIGHT_GREY};
    text-align: left;
    font-weight: 600;
    white-space: nowrap;
  }

  td {
    padding: 0.35rem 0.4rem;
    border-bottom: 1px solid ${Color.LIGHTER_GREY};
    vertical-align: middle;
  }
`;

const Cella = styled.td<{ $stato: StatoCella; $modificabile: boolean }>`
  background-color: ${({ $stato }) => sfondoCella[$stato]};
  cursor: ${({ $modificabile }) => ($modificabile ? "text" : "default")};
  overflow-wrap: anywhere;

  &:hover {
    outline: ${({ $modificabile }) =>
      $modificabile ? `1px solid ${Color.LIGHT_BLUE}` : "none"};
  }
`;

const Vuota = styled.span`
  color: ${Color.GREY};
  font-style: italic;
`;

const Campo = styled.input`
  width: 100%;
  padding: 0.25rem 0.4rem;
  border: 1px solid ${Color.BLUE};
  border-radius: 0.4rem;
  font-family: inherit;
  font-size: 0.85rem;
  box-sizing: border-box;
`;

const Scelta = styled.select`
  padding: 0.25rem 0.4rem;
  border: 1px solid ${Color.LIGHT_GREY};
  border-radius: 0.4rem;
  font-family: inherit;
  font-size: 0.85rem;
`;

const Applica = styled.button`
  padding: 0.3rem 0.9rem;
  border-style: hidden;
  border-radius: 0.5rem;
  background-color: ${Color.BLUE};
  color: ${Color.WHITE};
  font-family: inherit;
  font-size: 0.85rem;
  cursor: pointer;

  &:disabled {
    background-color: ${Color.LIGHT_GREY};
    cursor: default;
  }
`;

const NumeroRiga = styled.span<{ $colore: string }>`
  display: inline-block;
  min-width: 2.2rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0.4rem;
  background-color: ${({ $colore }) => $colore};
  color: ${Color.WHITE};
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
`;

const NotaRiga = styled.span`
  color: ${Color.RED};
`;

interface CellaModificabileProps {
  valore: string | null;
  tipo: Colonna["tipo"];
  stato: StatoCella;
  suggerimento: string;
  modificabile: boolean;
  onSalva: (valore: string) => void;
}

/*
 Una cella che al clic diventa un campo. Invio o uscita dal campo salvano, Esc lascia com'era.
 Esc non deve arrivare al dialogo, che altrimenti si chiuderebbe con la correzione a meta'.
*/
const CellaModificabile = ({
  valore,
  tipo,
  stato,
  suggerimento,
  modificabile,
  onSalva,
}: CellaModificabileProps) => {
  const [inModifica, setInModifica] = useState(false);
  const [bozza, setBozza] = useState("");
  const annullata = useRef(false);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inModifica) {
      campo.current?.focus();
    }
  }, [inModifica]);

  const chiudi = () => {
    setInModifica(false);
    if (annullata.current) {
      return;
    }
    if (bozza.trim() !== (valore ?? "").trim()) {
      onSalva(bozza.trim());
    }
  };

  if (inModifica) {
    return (
      <Cella $stato={stato} $modificabile>
        <Campo
          ref={campo}
          type={tipo}
          value={bozza}
          onChange={(evento) => setBozza(evento.target.value)}
          onBlur={chiudi}
          onKeyDown={(evento) => {
            if (evento.key === "Enter") {
              evento.currentTarget.blur();
            } else if (evento.key === "Escape") {
              evento.stopPropagation();
              annullata.current = true;
              evento.currentTarget.blur();
            }
          }}
        />
      </Cella>
    );
  }

  const mostrato = tipo === "date" ? comeData(valore) : valore;
  return (
    <Cella
      $stato={stato}
      $modificabile={modificabile}
      title={modificabile ? suggerimento : undefined}
      onClick={() => {
        if (!modificabile) {
          return;
        }
        annullata.current = false;
        setBozza(valore ?? "");
        setInModifica(true);
      }}
    >
      {mostrato ? mostrato : <Vuota>—</Vuota>}
    </Cella>
  );
};

interface Props {
  analisi: EmployeeImportAnalysis;
  // Falso a importazione conclusa, o mentre una correzione e' in viaggio.
  modificabile: boolean;
  // Vero se la correzione e' andata: la selezione si svuota solo in quel caso.
  onCorreggi: (celle: CellaCorretta[]) => Promise<boolean>;
}

const TabellaRighe = ({ analisi, modificabile, onCorreggi }: Props) => {
  const { t } = useTranslation();
  const [filtro, setFiltro] = useState<Filtro>("tutte");
  const [selezionate, setSelezionate] = useState<Set<string>>(new Set());
  const [campoDiGruppo, setCampoDiGruppo] =
    useState<CampoCorreggibile>("mansione");
  const [valoreDiGruppo, setValoreDiGruppo] = useState("");

  const colonne: Colonna[] = useMemo(
    () => [
      {
        campo: "cognome",
        titolo: t("import-chat-colonna-cognome"),
        valore: (riga) => riga.lastName,
        nomiNelBackend: ["cognome"],
        tipo: "text",
        larghezza: "15%",
      },
      {
        campo: "nome",
        titolo: t("import-chat-colonna-nome"),
        valore: (riga) => riga.firstName,
        nomiNelBackend: ["nome"],
        tipo: "text",
        larghezza: "12%",
      },
      {
        campo: "codicefiscale",
        titolo: t("import-chat-colonna-cf"),
        valore: (riga) => riga.fiscalCode,
        nomiNelBackend: ["codice fiscale"],
        tipo: "text",
        larghezza: "17%",
      },
      {
        campo: "email",
        titolo: t("import-chat-colonna-email"),
        valore: (riga) => riga.email,
        nomiNelBackend: ["e-mail"],
        tipo: "email",
        larghezza: "20%",
      },
      {
        campo: "mansione",
        titolo: t("import-chat-colonna-mansione"),
        valore: (riga) => riga.jobDescription,
        nomiNelBackend: ["mansione"],
        tipo: "text",
        larghezza: "13%",
      },
      {
        campo: "sede",
        titolo: t("import-chat-colonna-sede"),
        valore: (riga) => riga.workplace,
        nomiNelBackend: ["sede lavoro", "sede"],
        tipo: "text",
        larghezza: "11%",
      },
      {
        campo: "dataassunzione",
        titolo: t("import-chat-colonna-assunzione"),
        valore: (riga) => riga.hireDate,
        nomiNelBackend: ["data di assunzione"],
        tipo: "date",
        larghezza: "12%",
      },
    ],
    [t]
  );

  const piuFogli =
    new Set(analisi.rows.map((riga) => riga.sheet ?? "")).size > 1;

  const righeVisibili = analisi.rows.filter((riga) => {
    switch (filtro) {
      case "tutte":
        return true;
      case "da-completare":
        return daCompletare(riga);
      default:
        return riga.outcome === filtro;
    }
  });

  const selezionabili = righeVisibili.filter((riga) => !illeggibile(riga));
  const tutteSelezionate =
    selezionabili.length > 0 &&
    selezionabili.every((riga) => selezionate.has(chiaveDi(riga)));

  const statoDi = (riga: EmployeeImportRow, colonna: Colonna): StatoCella => {
    const problema = riga.problems.find(
      (p) => p.field !== null && colonna.nomiNelBackend.includes(p.field)
    );
    if (problema) {
      return problema.severity === "BLOCKING" ? "bloccante" : "mancante";
    }
    const cambiata = riga.changes.some((cambio) =>
      colonna.nomiNelBackend.includes(cambio.field)
    );
    return cambiata ? "cambiata" : "normale";
  };

  const suggerimentoDi = (riga: EmployeeImportRow, colonna: Colonna) => {
    const cambio = riga.changes.find((c) =>
      colonna.nomiNelBackend.includes(c.field)
    );
    const prima = cambio
      ? t("import-chat-prima", { valore: cambio.oldValue ?? "—" }) + "\n"
      : "";
    return prima + t("import-chat-clic-per-correggere");
  };

  const correggiCella = (
    riga: EmployeeImportRow,
    campo: CampoCorreggibile,
    valore: string
  ) => {
    void onCorreggi([{ sheet: riga.sheet, row: riga.row, field: campo, value: valore }]);
  };

  const applicaAlGruppo = async () => {
    const celle: CellaCorretta[] = analisi.rows
      .filter((riga) => selezionate.has(chiaveDi(riga)))
      .map((riga) => ({
        sheet: riga.sheet,
        row: riga.row,
        field: campoDiGruppo,
        value: valoreDiGruppo.trim(),
      }));
    if (await onCorreggi(celle)) {
      setSelezionate(new Set());
      setValoreDiGruppo("");
    }
  };

  const cambiaSelezione = (chiave: string) => {
    setSelezionate((precedenti) => {
      const nuove = new Set(precedenti);
      if (nuove.has(chiave)) {
        nuove.delete(chiave);
      } else {
        nuove.add(chiave);
      }
      return nuove;
    });
  };

  const filtri: { filtro: Filtro; etichetta: string; colore: string }[] = [
    {
      filtro: "tutte",
      etichetta: t("import-chat-filtro-tutte", { numero: analisi.rowsRead }),
      colore: Color.DARK_GREY,
    },
    {
      filtro: "TO_CREATE",
      etichetta: t("import-chat-da-creare", { numero: analisi.toCreate }),
      colore: Color.GREEN,
    },
    {
      filtro: "TO_UPDATE",
      etichetta: t("import-chat-da-aggiornare", { numero: analisi.toUpdate }),
      colore: Color.BLUE,
    },
    {
      filtro: "REJECTED",
      etichetta: t("import-chat-fuori", { numero: analisi.rejected }),
      colore: Color.RED,
    },
    {
      filtro: "da-completare",
      etichetta: t("import-chat-filtro-da-completare", {
        numero: analisi.rows.filter(daCompletare).length,
      }),
      colore: Color.ORANGE,
    },
  ];

  const colonnaDiGruppo = colonne.find((c) => c.campo === campoDiGruppo);

  return (
    <Contenitore>
      <Filtri>
        {filtri.map((voce) => (
          <Chip
            key={voce.filtro}
            type="button"
            $colore={voce.colore}
            $attivo={filtro === voce.filtro}
            onClick={() => setFiltro(voce.filtro)}
          >
            {voce.etichetta}
          </Chip>
        ))}
      </Filtri>

      {analisi.fileProblems.map((problema) => (
        <ProblemaFile key={problema}>{problema}</ProblemaFile>
      ))}

      {selezionate.size > 0 && modificabile && (
        <BarraGruppo>
          <strong>
            {t("import-chat-selezionate", { numero: selezionate.size })}
          </strong>
          <Scelta
            aria-label={t("import-chat-campo-di-gruppo")}
            value={campoDiGruppo}
            onChange={(evento) => {
              setCampoDiGruppo(evento.target.value as CampoCorreggibile);
              setValoreDiGruppo("");
            }}
          >
            {CAMPI_DI_GRUPPO.map((campo) => (
              <option key={campo} value={campo}>
                {colonne.find((c) => c.campo === campo)?.titolo}
              </option>
            ))}
          </Scelta>
          <Campo
            style={{ width: "14rem" }}
            type={colonnaDiGruppo?.tipo ?? "text"}
            value={valoreDiGruppo}
            placeholder={t("import-chat-valore-per-tutte")}
            onChange={(evento) => setValoreDiGruppo(evento.target.value)}
          />
          <Applica
            type="button"
            disabled={!valoreDiGruppo.trim()}
            onClick={() => void applicaAlGruppo()}
          >
            {t("import-chat-applica")}
          </Applica>
        </BarraGruppo>
      )}

      <Scorrimento>
        <Tabella>
          <thead>
            <tr>
              <th style={{ width: "2.2rem" }}>
                <input
                  type="checkbox"
                  aria-label={t("import-chat-filtro-tutte", {
                    numero: selezionabili.length,
                  })}
                  disabled={!modificabile || selezionabili.length === 0}
                  checked={tutteSelezionate}
                  onChange={() =>
                    setSelezionate(
                      tutteSelezionate
                        ? new Set()
                        : new Set(selezionabili.map(chiaveDi))
                    )
                  }
                />
              </th>
              <th style={{ width: piuFogli ? "7rem" : "4rem" }}>
                {t("import-chat-colonna-riga")}
              </th>
              {colonne.map((colonna) => (
                <th key={colonna.campo} style={{ width: colonna.larghezza }}>
                  {colonna.titolo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {righeVisibili.length === 0 && (
              <tr>
                <td colSpan={colonne.length + 2}>
                  <Vuota>{t("import-chat-nessuna-riga")}</Vuota>
                </td>
              </tr>
            )}
            {righeVisibili.map((riga) => {
              const chiave = chiaveDi(riga);
              const etichettaRiga = piuFogli
                ? `${riga.sheet ?? ""} · ${riga.row}`
                : String(riga.row);
              const numero = (
                <NumeroRiga $colore={coloreEsito[riga.outcome]}>
                  {etichettaRiga}
                </NumeroRiga>
              );
              return (
                <tr key={chiave}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={t("import-chat-riga", { numero: etichettaRiga })}
                      disabled={!modificabile || illeggibile(riga)}
                      checked={selezionate.has(chiave)}
                      onChange={() => cambiaSelezione(chiave)}
                    />
                  </td>
                  <td>
                    {riga.problems.length > 0 ? (
                      <Suggerimento
                        testo={riga.problems.map((p) => p.message).join(" ")}
                      >
                        {numero}
                      </Suggerimento>
                    ) : (
                      numero
                    )}
                  </td>
                  {illeggibile(riga) ? (
                    <td colSpan={colonne.length}>
                      <NotaRiga>
                        {riga.problems.map((p) => p.message).join(" ")}
                      </NotaRiga>
                    </td>
                  ) : (
                    colonne.map((colonna) => (
                      <CellaModificabile
                        // Il valore nella chiave rimonta la cella quando il backend lo cambia.
                        key={`${colonna.campo}-${colonna.valore(riga) ?? ""}`}
                        valore={colonna.valore(riga)}
                        tipo={colonna.tipo}
                        stato={statoDi(riga, colonna)}
                        suggerimento={suggerimentoDi(riga, colonna)}
                        modificabile={modificabile}
                        onSalva={(valore) =>
                          correggiCella(riga, colonna.campo, valore)
                        }
                      />
                    ))
                  )}
                </tr>
              );
            })}
          </tbody>
        </Tabella>
      </Scorrimento>
    </Contenitore>
  );
};

export default TabellaRighe;
